import { useState, useEffect, useRef } from "react";

const API = "/api";

const emptyForm = {
  name: "", slug: "", type: "Villa", tagline: "", description: "",
  location: "", city: "", region: "", heroImage: "", heroVideo: "",
  images: [], amenities: [], beds24Url: "", beds24PropId: "",
  priceFrom: "", rating: 5.0, reviewCount: 0, featured: false, active: true,
  rooms: []
};

export default function AdminProperties() {
  const [properties, setProperties] = useState([]);
  const [view, setView] = useState("list");
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [amenityInput, setAmenityInput] = useState("");
  const [uploading, setUploading] = useState("");

  const heroImageRef = useRef();
  const heroVideoRef = useRef();
  const galleryRef = useRef();

  const token = localStorage.getItem("adminToken");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchProperties(); }, []);

  async function fetchProperties() {
    const res = await fetch(`${API}/properties/admin/all`, { headers });
    const data = await res.json();
    setProperties(data.properties || data || []);
  }

  async function uploadFile(file, type) {
    setUploading(type);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/upload`, { method: "POST", headers: authHeaders, body: fd });
      const data = await res.json();
      setUploading("");
      return data.path || null;
    } catch (e) {
      setUploading("");
      setErr("Upload failed: " + e.message);
      return null;
    }
  }

  async function handleHeroImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const path = await uploadFile(file, "heroImage");
    if (path) setForm(f => ({ ...f, heroImage: path }));
  }

  async function handleHeroVideoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const path = await uploadFile(file, "heroVideo");
    if (path) setForm(f => ({ ...f, heroVideo: path }));
  }

  async function handleGalleryUpload(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading("gallery");
    const paths = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/upload`, { method: "POST", headers: authHeaders, body: fd });
      const data = await res.json();
      if (data.path) paths.push(data.path);
    }
    setUploading("");
    setForm(f => ({ ...f, images: [...f.images, ...paths] }));
  }

  async function handleRoomImagesUpload(roomIndex, e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading("room-" + roomIndex);
    const paths = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/upload`, { method: "POST", headers: authHeaders, body: fd });
      const data = await res.json();
      if (data.path) paths.push(data.path);
    }
    setUploading("");
    const rooms = [...form.rooms];
    rooms[roomIndex] = { ...rooms[roomIndex], images: [...(rooms[roomIndex].images || []), ...paths] };
    setForm(f => ({ ...f, rooms }));
  }

  function removeGalleryImage(i) {
    setForm(f => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  }

  function removeRoomImage(roomIndex, imgIndex) {
    const rooms = [...form.rooms];
    rooms[roomIndex] = { ...rooms[roomIndex], images: rooms[roomIndex].images.filter((_, i) => i !== imgIndex) };
    setForm(f => ({ ...f, rooms }));
  }

  function addAmenity() {
    if (!amenityInput.trim()) return;
    setForm(f => ({ ...f, amenities: [...f.amenities, amenityInput.trim()] }));
    setAmenityInput("");
  }

  function removeAmenity(i) {
    setForm(f => ({ ...f, amenities: f.amenities.filter((_, idx) => idx !== i) }));
  }

  function addRoom() {
    setForm(f => ({ ...f, rooms: [...f.rooms, { label: "", description: "", images: [] }] }));
  }

  function removeRoom(i) {
    setForm(f => ({ ...f, rooms: f.rooms.filter((_, idx) => idx !== i) }));
  }

  function updateRoom(i, field, val) {
    const rooms = [...form.rooms];
    rooms[i] = { ...rooms[i], [field]: val };
    setForm(f => ({ ...f, rooms }));
  }

  function startNew() {
    setForm(emptyForm);
    setEditId(null);
    setMsg(""); setErr("");
    setView("form");
  }

  function startEdit(p) {
    setForm({
      name: p.name || "", slug: p.slug || "", type: p.type || "Villa",
      tagline: p.tagline || "", description: p.description || "",
      location: p.location || "", city: p.city || "", region: p.region || "",
      heroImage: p.heroImage || "", heroVideo: p.heroVideo || "",
      images: p.images || [], amenities: p.amenities || [],
      beds24Url: p.beds24Url || "", beds24PropId: p.beds24PropId || "",
      priceFrom: p.priceFrom || "", rating: p.rating || 5.0,
      reviewCount: p.reviewCount || 0, featured: p.featured || false,
      active: p.active !== false,
      rooms: (p.rooms || []).map(r => ({
        label: r.label || "",
        description: r.description || "",
        images: r.images || []
      }))
    });
    setEditId(p._id);
    setMsg(""); setErr("");
    setView("form");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setMsg(""); setErr("");
    try {
      const url = editId ? `${API}/properties/${editId}` : `${API}/properties`;
      const method = editId ? "PUT" : "POST";
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Error saving");
      setMsg(editId ? "Property updated!" : "Property created!");
      fetchProperties();
      setTimeout(() => setView("list"), 1500);
    } catch (err) { setErr(err.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this property?")) return;
    await fetch(`${API}/properties/${id}`, { method: "DELETE", headers });
    fetchProperties();
  }

  async function toggleFeatured(id) {
    try {
      const res = await fetch(`${API}/properties/${id}/featured`, { method: "PATCH", headers });
      if (!res.ok) throw new Error("Failed to toggle");
      fetchProperties();
    } catch (e) {
      console.error("Toggle featured failed:", e.message);
    }
  }

  const uploadBtnStyle = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "9px 16px", background: "rgba(200,149,108,0.1)",
    color: "#c8956c", border: "1.5px dashed rgba(200,149,108,0.4)",
    borderRadius: 10, fontSize: "0.85rem", cursor: "pointer",
    fontFamily: "DM Sans, sans-serif", marginTop: 8, transition: "all 0.2s"
  };

  const previewGridStyle = {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))",
    gap: 8, marginTop: 12
  };

  const previewItemStyle = {
    position: "relative", borderRadius: 8, overflow: "hidden", height: 80
  };

  const previewRemoveBtnStyle = {
    position: "absolute", top: 4, right: 4, background: "rgba(28,23,18,0.7)",
    color: "#fff", border: "none", borderRadius: "50%", width: 20, height: 20,
    fontSize: "0.7rem", cursor: "pointer", display: "flex",
    alignItems: "center", justifyContent: "center", lineHeight: 1
  };

  if (view === "form") return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
        <button className="admin-btn admin-btn-sm" onClick={() => setView("list")}>← Back</button>
        <div className="admin-section-title">{editId ? "Edit Property" : "Add New Property"}</div>
      </div>

      {msg && <div className="admin-success">{msg}</div>}
      {err && <div className="admin-error-msg">{err}</div>}

      <form onSubmit={handleSubmit} onKeyDown={e => { if (e.key === "Enter" && e.target.tagName !== "BUTTON") e.preventDefault(); }}>

        {/* BASIC INFO */}
        <div className="admin-card">
          <h3 style={{ marginBottom: 20, color: "#1c1712", fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "1.3rem" }}>Basic Information</h3>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label>Property Name</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Vila Eva" required />
            </div>
            <div className="admin-field">
              <label>Slug (URL)</label>
              <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} placeholder="vila-eva" required />
            </div>
          </div>
          <div className="admin-grid-3">
            <div className="admin-field">
              <label>Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                <option>Villa</option><option>Apartment</option><option>Studio</option><option>Room</option>
              </select>
            </div>
            <div className="admin-field">
              <label>City</label>
              <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Kavaje" />
            </div>
            <div className="admin-field">
              <label>Region</label>
              <input value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))} placeholder="Albanian Riviera" />
            </div>
          </div>
          <div className="admin-field">
            <label>Location (display text)</label>
            <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Golem, Kavaje" />
          </div>
          <div className="admin-field">
            <label>Tagline</label>
            <input value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} placeholder="Seaside luxury on the Albanian Riviera" />
          </div>
          <div className="admin-field">
            <label>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Full property description..." />
          </div>
        </div>

        {/* PRICING */}
        <div className="admin-card">
          <h3 style={{ marginBottom: 20, color: "#1c1712", fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "1.3rem" }}>Pricing & Reviews</h3>
          <div className="admin-grid-3">
            <div className="admin-field">
              <label>Price From (EUR/night)</label>
              <input type="number" value={form.priceFrom} onChange={e => setForm(f => ({ ...f, priceFrom: e.target.value }))} placeholder="120" />
            </div>
            <div className="admin-field">
              <label>Rating</label>
              <input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={e => setForm(f => ({ ...f, rating: e.target.value }))} />
            </div>
            <div className="admin-field">
              <label>Review Count</label>
              <input type="number" value={form.reviewCount} onChange={e => setForm(f => ({ ...f, reviewCount: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 24, marginTop: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.9rem", color: "#1c1712" }}>
              <input type="checkbox" checked={form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))} />
              Featured on homepage
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.9rem", color: "#1c1712" }}>
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
              Active (visible on site)
            </label>
          </div>
        </div>

        {/* BEDS24 */}
        <div className="admin-card">
          <h3 style={{ marginBottom: 20, color: "#1c1712", fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "1.3rem" }}>Beds24 Booking</h3>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label>Beds24 Property ID</label>
              <input value={form.beds24PropId} onChange={e => setForm(f => ({ ...f, beds24PropId: e.target.value }))} placeholder="12345" />
            </div>
            <div className="admin-field">
              <label>Beds24 Booking URL</label>
              <input value={form.beds24Url} onChange={e => setForm(f => ({ ...f, beds24Url: e.target.value }))} placeholder="https://beds24.com/booking2.php?..." />
            </div>
          </div>
        </div>

        {/* MEDIA */}
        <div className="admin-card">
          <h3 style={{ marginBottom: 20, color: "#1c1712", fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "1.3rem" }}>Media</h3>

          <div className="admin-field">
            <label>Hero Image</label>
            <input value={form.heroImage} onChange={e => setForm(f => ({ ...f, heroImage: e.target.value }))} placeholder="/vila-eva-hero.jpg" />
            <input type="file" accept="image/*" ref={heroImageRef} style={{ display: "none" }} onChange={handleHeroImageUpload} />
            <button type="button" style={uploadBtnStyle} onClick={() => heroImageRef.current.click()}>
              {uploading === "heroImage" ? "Uploading..." : "📁 Upload Hero Image"}
            </button>
            {form.heroImage && (
              <div style={{ marginTop: 10 }}>
                <img src={form.heroImage} alt="hero preview" style={{ height: 120, borderRadius: 8, objectFit: "cover", maxWidth: "100%" }} />
              </div>
            )}
          </div>

          <div className="admin-field">
            <label>Hero Video (.mp4)</label>
            <input value={form.heroVideo} onChange={e => setForm(f => ({ ...f, heroVideo: e.target.value }))} placeholder="/vila-eva.mp4" />
            <input type="file" accept="video/*" ref={heroVideoRef} style={{ display: "none" }} onChange={handleHeroVideoUpload} />
            <button type="button" style={uploadBtnStyle} onClick={() => heroVideoRef.current.click()}>
              {uploading === "heroVideo" ? "Uploading..." : "📁 Upload Hero Video"}
            </button>
            {form.heroVideo && (
              <div style={{ marginTop: 10 }}>
                <video src={form.heroVideo} style={{ height: 120, borderRadius: 8, maxWidth: "100%" }} muted playsInline controls />
              </div>
            )}
          </div>

          <div className="admin-field">
            <label>Gallery Images</label>
            <input type="file" accept="image/*" multiple ref={galleryRef} style={{ display: "none" }} onChange={handleGalleryUpload} />
            <button type="button" style={uploadBtnStyle} onClick={() => galleryRef.current.click()}>
              {uploading === "gallery" ? "Uploading..." : "📁 Upload Gallery Photos"}
            </button>
            {form.images.length > 0 && (
              <div style={previewGridStyle}>
                {form.images.map((img, i) => (
                  <div key={i} style={previewItemStyle}>
                    <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <button type="button" style={previewRemoveBtnStyle} onClick={() => removeGalleryImage(i)}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ROOMS */}
        <div className="admin-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ color: "#1c1712", fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "1.3rem" }}>Rooms</h3>
            <button type="button" className="admin-btn admin-btn-sm admin-btn-gold" onClick={addRoom}>+ Add Room</button>
          </div>
          <p style={{ fontSize: "0.85rem", color: "rgba(28,23,18,0.5)", marginBottom: 20 }}>Each room has a title, description, and multiple photos. Clicking a room on the property page opens a photo popup.</p>

          {form.rooms.length === 0 && (
            <p style={{ color: "rgba(28,23,18,0.35)", fontSize: "0.88rem", textAlign: "center", padding: "20px 0" }}>No rooms added yet. Click "+ Add Room" to start.</p>
          )}

          {form.rooms.map((room, i) => (
            <div key={i} style={{ background: "rgba(200,149,108,0.04)", border: "1px solid rgba(200,149,108,0.15)", borderRadius: 14, padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#c8956c", textTransform: "uppercase", letterSpacing: 1 }}>Room {i + 1}</span>
                <button type="button" className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => removeRoom(i)}>Remove</button>
              </div>

              <div className="admin-field">
                <label>Room Title</label>
                <input value={room.label || ""} onChange={e => updateRoom(i, "label", e.target.value)} placeholder="e.g. Suite with Sea View" />
              </div>

              <div className="admin-field">
                <label>Room Description</label>
                <textarea
                  value={room.description || ""}
                  onChange={e => updateRoom(i, "description", e.target.value)}
                  placeholder="Describe this room — size, bed type, view, special features..."
                  style={{ minHeight: 80 }}
                />
              </div>

              <div className="admin-field" style={{ marginBottom: 0 }}>
                <label>Room Photos</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  id={"room-upload-" + i}
                  style={{ display: "none" }}
                  onChange={e => handleRoomImagesUpload(i, e)}
                />
                <button
                  type="button"
                  style={uploadBtnStyle}
                  onClick={() => document.getElementById("room-upload-" + i).click()}
                >
                  {uploading === "room-" + i ? "Uploading..." : "📁 Upload Room Photos"}
                </button>

                {(room.images || []).length > 0 && (
                  <div style={previewGridStyle}>
                    {room.images.map((img, j) => (
                      <div key={j} style={previewItemStyle}>
                        <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        <button type="button" style={previewRemoveBtnStyle} onClick={() => removeRoomImage(i, j)}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* AMENITIES */}
        <div className="admin-card">
          <h3 style={{ marginBottom: 20, color: "#1c1712", fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "1.3rem" }}>Amenities</h3>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input
              value={amenityInput}
              onChange={e => setAmenityInput(e.target.value)}
              placeholder="e.g. Private Pool"
              onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addAmenity())}
              style={{ flex: 1, padding: "10px 14px", border: "1.5px solid rgba(200,149,108,0.2)", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: "0.9rem", outline: "none" }}
            />
            <button type="button" className="admin-btn admin-btn-sm" onClick={addAmenity}>Add</button>
          </div>
          <div className="admin-tag-input">
            {form.amenities.map((a, i) => (
              <div key={i} className="admin-tag">
                {a} <button type="button" onClick={() => removeAmenity(i)}>×</button>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button type="submit" className="admin-btn admin-btn-gold">{editId ? "Save Changes" : "Create Property"}</button>
          <button type="button" className="admin-btn" style={{ background: "transparent", color: "#1c1712", border: "1px solid rgba(28,23,18,0.15)" }} onClick={() => setView("list")}>Cancel</button>
        </div>

      </form>
    </div>
  );

  // LIST VIEW
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
        <div>
          <div className="admin-section-title">Properties</div>
          <div className="admin-section-sub">{properties.length} properties total</div>
        </div>
        <button className="admin-btn admin-btn-gold" onClick={startNew}>+ Add Property</button>
      </div>

      {properties.map(p => (
        <div key={p._id} className="admin-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {p.heroImage && <img src={p.heroImage} alt={p.name} style={{ width: 72, height: 56, objectFit: "cover", borderRadius: 8 }} />}
            <div>
              <div style={{ fontWeight: 600, color: "#1c1712", marginBottom: 2 }}>{p.name}</div>
              <div style={{ fontSize: "0.82rem", color: "rgba(28,23,18,0.5)" }}>{p.location} · {p.type} · from €{p.priceFrom}/night</div>
              <div style={{ fontSize: "0.78rem", marginTop: 4, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: p.active ? "#27ae60" : "#c0392b" }}>
                  {p.active ? "● Active" : "● Inactive"}
                </span>
                <button
                  onClick={() => toggleFeatured(p._id)}
                  style={{
                    background: p.featured ? "rgba(200,149,108,0.12)" : "transparent",
                    border: "1px solid " + (p.featured ? "#c8956c" : "rgba(28,23,18,0.15)"),
                    borderRadius: "100px",
                    padding: "2px 10px",
                    fontSize: "0.72rem",
                    color: p.featured ? "#c8956c" : "rgba(28,23,18,0.35)",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    fontFamily: "DM Sans, sans-serif",
                  }}
                >
                  {p.featured ? "⭐ Featured" : "☆ Set Featured"}
                </button>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="admin-btn admin-btn-sm" onClick={() => startEdit(p)}>Edit</button>
            <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => handleDelete(p._id)}>Delete</button>
          </div>
        </div>
      ))}

      {properties.length === 0 && (
        <div className="admin-card" style={{ textAlign: "center", padding: 60, color: "rgba(28,23,18,0.4)" }}>
          No properties yet. Click "Add Property" to get started!
        </div>
      )}
    </div>
  );
}